import React, { useEffect, useMemo, useRef, useState } from "react";
import { ModalShell } from "./ModalShell";
import { Orders } from "../../models/Orders";
import { Company } from "../../models/Company";
import { SupplyDTO } from "../../models/SupplyDTO";
import { apiFetch } from "../../utils/api";
import { BASE_URLS } from "../../config/urls";
import { BoxesForm } from "../forms/BoxesForm";
import { BoxInput, SupplyType } from "../../types/form";
import { RadioGroup } from "../RadioGroup";
import {PhoneForm} from "../forms/PhoneForm";
import {Person} from "../../models/Person";
import {PriceRequest} from "../../models/PriceRequest"; // путь поправь под себя
import {useOrderSubmission} from "../../hooks/useOrderSubmission";
import {BtnSpinner} from "../fields/BtnSpinner";
import {useDocumentLinks} from "../../context/DocLinksContext";

type PAYMENT = "ONLINE" | "OFFICE";

function parseNum(v: any) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function normalizeBoxes(boxes: any[] | undefined): BoxInput[] {
    if (!Array.isArray(boxes) || boxes.length === 0) {
        return [{ length: 0, width: 0, height: 0, amount: 0 }];
    }
    return boxes.map((b) => ({
        length: parseNum(b.length),
        width: parseNum(b.width),
        height: parseNum(b.height),
        amount: parseNum(b.amount),
    }));
}

function boxesValid(inputs: BoxInput[]) {
    if (!inputs.length) return false;
    return inputs.every((b) => b.length > 0 && b.width > 0 && b.height > 0 && b.amount > 0);
}

function phoneValidMaskedOrRaw(p: string) {
    const digits = (p ?? "").replace(/\D/g, "");
    return digits.length >= 10;
}

function companyLabel(c: any) {
    const title = c?.title ?? c?.companyName ?? c?.name ?? "Компания";
    const inn = c?.inn ? ` (${c.inn})` : "";
    return `${title}${inn}`;
}

export function OrderUpsertModal({
                                     isOpen,
                                     onClose,
                                     supply,          // ✅ обязательно: именно отсюда warehouses
                                     order,           // undefined => создание, иначе редактирование
                                     companies,
                                     person,
                                     onSaved,
                                 }: {
    isOpen: boolean;
    onClose: () => void;
    supply: SupplyDTO;
    order?: Orders;
    companies: Company[];
    person: Person;
    onSaved: (saved: Orders) => void;
}) {
    const companiesBlockRef = useRef<HTMLDivElement | null>(null);
    const phoneBlockRef = useRef<HTMLDivElement | null>(null);
    const boxesErrorRef = useRef<HTMLDivElement | null>(null);
    const ozonRef = useRef<HTMLInputElement | null>(null);
    const commentRef = useRef<HTMLTextAreaElement | null>(null);
    const agreeRef = useRef<HTMLInputElement | null>(null);
    const errRef = useRef<HTMLDivElement | null>(null);
    const docLinks = useDocumentLinks();

    const warehouses = useMemo(() => (supply?.warehouses ?? []).filter(Boolean) as any[], [supply]);

    const [entityIndex, setEntityIndex] = useState(0);
    const [selectedWarehouseIndex, setSelectedWarehouseIndex] = useState(0);

    const [tel, setTel] = useState("");
    const [telError, setTelError] = useState("");

    const [inputs, setInputs] = useState<BoxInput[]>([{ length: 0, width: 0, height: 0, amount: 0 }]);
    const [inputsValid, setInputsValid] = useState(true);

    const [supplyType, setSupplyType] = useState<SupplyType>("Короб");
    const pallet = supplyType === 'Монопаллет' || supplyType === 'Паллета';

    const [ozonNumber, setOzonNumber] = useState("");
    const [ozonError, setOzonError] = useState("");

    const [agree, setAgree] = useState(false);
    const [agreeError, setAgreeError] = useState("");

    const [willTaken, setWillTaken] = useState(false);
    const [paymentOnline, setPaymentOnline] = useState(false);

    const [comment, setComment] = useState("");
    const [commentError, setCommentError] = useState("");

    const [isSubmitting, setSubmitting] = useState(false);
    const [isPaying, setPaying] = useState(false);

    const [err, setErr] = useState("");
    const [ok, setOk] = useState("");

    const selectedWarehouse = warehouses[selectedWarehouseIndex];
    const storeIsOzon = String(selectedWarehouse?.store ?? "") === "Ozon";
    const {submit} = useOrderSubmission();

    // ✅ init как в твоём старом JS: company + склад
    useEffect(() => {
        if (!isOpen) return;

        setErr("");
        setOk("");
        setSubmitting(false);
        setPaying(false);

        // phone
        let p = order?.phoneNumber ?? person?.phone ?? '';
        p = p ? `(${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}` : '';
        setTel(p);

        setTelError("");

        // boxes
        setInputs(normalizeBoxes((order as any)?.boxes));
        setInputsValid(true);

        // supplyType
        setSupplyType(((order as any)?.supplyType as SupplyType) ?? "Короб");

        // isOzon
        setOzonNumber(String((order as any)?.numberOzon ?? ""));
        setOzonError("");

        // flags
        setWillTaken(!!(order as any)?.willTaken);
        setPaymentOnline(!!(order as any)?.paymentSite);

        // comment
        setComment(String((order as any)?.comment ?? ""));
        setCommentError("");

        // ✅ склад: findIndex by store + sendCity (как у тебя было)
        if (order) {
            const idx = warehouses.findIndex(
                (w) => w?.store === (order as any)?.store && w?.sendCity === (order as any)?.sendCity
            );
            setSelectedWarehouseIndex(Math.max(0, idx));
        } else {
            setSelectedWarehouseIndex(0);
        }

        // ✅ company: старая логика — ищем по inn / entity
        if (companies?.length && order) {
            const currInn = String((order as any)?.inn ?? "");
            const currEntity = String((order as any)?.entity ?? "");

            const idx = companies.findIndex((c: any) => {
                const innMatch = currInn && String(c?.inn ?? "") === currInn;
                const titleMatch = currEntity && companyLabel(c).includes(currEntity);
                return innMatch || titleMatch;
            });

            setEntityIndex(idx >= 0 ? idx : 0);
        } else {
            setEntityIndex(0);
        }
    }, [isOpen, order, companies, warehouses]);

    useEffect(() => {
        if (!isOpen) return;
        if (!err) return;

        // подождем рендер
        requestAnimationFrame(() => {
            errRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            errRef.current?.focus?.();
        });
    }, [err, isOpen]);
    
    if (!isOpen) return null;

    const validate = () => {
        setErr("");
        setOk("");
        setTelError("");
        setOzonError("");
        setCommentError("");
        setInputsValid(true);
        setAgreeError("");

        let first: HTMLElement | null = null;

        if (!companies?.length) {
            first = companiesBlockRef.current;
            setErr("Сначала добавьте компанию в личном кабинете.");
        }

        if (!phoneValidMaskedOrRaw(tel)) {
            setTelError("Введите корректный номер телефона");
            first = first ?? (phoneBlockRef.current as any);
        }

        if (!boxesValid(inputs)) {
            setInputsValid(false);
            first = first ?? (boxesErrorRef.current as any);
        }

        if (storeIsOzon && !ozonNumber.trim()) {
            setOzonError("Укажите номер заказа Ozon");
            first = first ?? (ozonRef.current as any);
        }

        // по твоей логике раньше: если willTaken, то comment обязателен
        if (willTaken && !comment.trim()) {
            setCommentError("Пожалуйста, укажите откуда забрать товар");
            first = first ?? (commentRef.current as any);
        }

        if (!agree) {
            setAgreeError("Необходимо согласиться с условиями и офертой");
            first = first ?? (agreeRef.current as any);
        }

        if (!selectedWarehouse) {
            setErr("Выберите склад назначения.");
            return false;
        }

        if (first) {
            first.scrollIntoView({ behavior: "smooth", block: "center" });
            (first as any).focus?.();
            return false;
        }

        return true;
    };

    const handleSaveOrPay = async (payNow: boolean) => {
        if (isSubmitting || isPaying) return;
        payNow ? setPaying(true) : setSubmitting(true);

        try {
            if (!validate()) return;

            const selectedCompany: any = companies[entityIndex];

            const wh = warehouses[selectedWarehouseIndex];
            const store = String(wh?.store ?? "");
            const sendCity = String(wh?.sendCity ?? wh?.warehouseName ?? "");

            // даты берём из supply (или из order/base, если у тебя там есть)
            const departureDate = new Date((supply as any)?.departureDate ?? (order as any)?.departureDate ?? new Date());
            const acceptanceDate = new Date((supply as any)?.acceptanceDate ?? (order as any)?.acceptanceDate ?? departureDate);

            const {volume, amount} = inputs.reduce(
                (acc, i) => ({
                    volume: acc.volume + i.length * i.width * i.height * i.amount,
                    amount: acc.amount + i.amount,
                }),
                {volume: 0, amount: 0}
            );
            const volumeM3 = volume / 1_000_000;

            // PriceRequest как в калькуляторе (подпиши параметры под свой конструктор)
            const priceReq = new PriceRequest(
                store,
                sendCity,
                volumeM3,
                willTaken,
                pallet,
                amount
            );

            // submit сам:
            // - buildBoxes(inputs)
            // - calculate(priceReq, pallet ? boxes : undefined)
            // - normalizePhone
            // - createOrder(...)
            // - orders.create(order, boxes)
            const { created } = await submit({
                priceReq,
                boxesInputs: inputs,
                pallet,
                supplyMeta: {
                    entity: selectedCompany?.title ?? selectedCompany?.companyName ?? selectedCompany?.name ?? "",
                    departureDate,
                    acceptanceDate,
                    phoneMasked: tel,

                    sendCity,
                    store,

                    supplyType,
                    willTaken,

                    payment: paymentOnline,          // если у тебя "payment" именно факт оплаты/онлайн — иначе поменяй
                    comment: (comment ?? "").trim(),
                    ozonNumber: (store === "Ozon" ? ozonNumber.trim() : ""),

                    supplyId: (supply as any)?.id ?? 0,

                    // чтобы hook понимал "редактирование", а не новый:
                    baseOrder: order ?? undefined,

                    // если тебе важно сохранять эти поля при редактировании:
                    paymentStatus: (order as any)?.paymentStatus,
                    changeable: (order as any)?.changeable,
                    status: (order as any)?.status,
                },
                now: new Date(),
            });

            setOk("Сохранено");
            onSaved(created);

            if (payNow) {
                window.location.assign(`${BASE_URLS.app}/payment/${(created as any)?.id ?? (order as any)?.id}`);
            } else {
                onClose();
            }
        } catch (e: any) {
            // ловим AFTER_CUTOFF из useOrderSubmission
            const code = e?.code || e?.message;
            if (code === "AFTER_CUTOFF") {
                setErr("После 22:00 нельзя создавать/изменять заказ на сегодня. Выберите другую дату приёмки/поставки.");
            } else {
                setErr(e?.message || "Ошибка сохранения");
            }
        } finally {
            setSubmitting(false);
            setPaying(false);
        }
    };

    return (
        <ModalShell
            title={order?.id ? "Изменить заказ" : "Новая поставка"}
            subtitle="Заполните все необходимые поля"
            onClose={onClose}
        >
            {err && (
                <div ref={errRef} tabIndex={-1} className="profile-error">{err}</div>
            )}
            {ok && <div className="profile-success">{ok}</div>}

            {/* Юр.лицо */}
            <div className="schedule_form_title" style={{ marginTop: 10, marginBottom: 6 }}>
                Юридическое лицо
            </div>

            <div ref={companiesBlockRef} tabIndex={-1}>
                {!companies.length ? (
                    <div className="profile-error" style={{ height: "auto" }}>
                        Вы еще не добавили компании.
                    </div>
                ) : (
                    <select
                        className="select"
                        value={String(entityIndex)}
                        onChange={(e) => setEntityIndex(parseInt(e.target.value, 10))}
                    >
                        {companies.map((c: any, i: number) => (
                            <option key={c?.inn ?? i} value={String(i)}>
                                {companyLabel(c)}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* Телефон */}
            <div className="schedule_form_title" style={{ marginTop: 14 }}>
                Номер телефона
            </div>
            <div ref={phoneBlockRef}>
                <PhoneForm
                    defaultValue={tel}
                    error={telError}
                    onChange={(v) => {
                        setTel(v);
                        setTelError("");
                    }}
                />
                {telError && <div className="profile-error" style={{ marginTop: 8 }}>{telError}</div>}
            </div>

            {/* Коробки (твой BoxesForm) */}
            <div className="schedule_form_title" style={{ marginTop: 14 }}>
                Размеры и количество коробок/паллет
            </div>

            {!inputsValid && (
                <div ref={boxesErrorRef} className="profile-error" style={{ marginTop: 8 }}>
                    Коробки заполнены не корректно
                </div>
            )}

            <BoxesForm
                inputs={inputs}
                onChange={(vals) => {
                    setInputs(vals);
                    setInputsValid(true);
                }}
            />

            {/* Тип поставки */}
            <div className="schedule_form_title" style={{ marginTop: 14 }}>
                Тип поставки
            </div>
            <RadioGroup
                name="supplyType"
                value={supplyType}
                options={[
                    { label: "Короб", value: "Короб" },
                    { label: "Монопаллет", value: "Монопаллет" },
                    { label: "Паллета", value: "Паллета" },
                ]}
                onChange={(v) => setSupplyType(v as SupplyType)}
            />

            {/* ✅ Склад назначения из supply.warehouses */}
            <div className="schedule_form_title" style={{ marginTop: 14 }}>
                Склад назначения
            </div>

            <RadioGroup
                name="warehouse"
                value={String(selectedWarehouseIndex)}
                options={warehouses.map((w: any, i: number) => ({
                    label: w.warehouseName,
                    value: String(i),
                }))}
                onChange={(v) => setSelectedWarehouseIndex(parseInt(v, 10))}
            />

            {/* Ozon */}
            {storeIsOzon && (
                <div style={{ marginTop: 14 }}>
                    <div className="schedule_form_title">Номер заказа (Ozon)</div>
                    <input
                        ref={ozonRef}
                        className="profile-input"
                        value={ozonNumber}
                        onChange={(e) => {
                            setOzonNumber(e.target.value);
                            setOzonError("");
                        }}
                        placeholder="Номер заказа"
                        aria-invalid={!!ozonError}
                    />
                    {ozonError && <div className="profile-error" style={{ marginTop: 8 }}>{ozonError}</div>}
                </div>
            )}

            {/* Забор */}
            <div className="schedule_form_title" style={{ marginTop: 14 }}>
                Забрать со склада
            </div>
            <RadioGroup
                name="take"
                value={willTaken ? "Да" : "Нет"}
                options={[
                    { label: "Да", value: "Да" },
                    { label: "Нет", value: "Нет" },
                ]}
                onChange={(v) => setWillTaken(v === "Да")}
            />

            {/* Оплата */}
            <div className="schedule_form_title" style={{ marginTop: 14 }}>
                Способ оплаты
            </div>
            <RadioGroup
                name="payment"
                value={paymentOnline ? "ONLINE" : "OFFICE"}
                options={[
                    { label: "Онлайн", value: "ONLINE" },
                    { label: "В офисе", value: "OFFICE" },
                ]}
                onChange={(v) => setPaymentOnline(v === "ONLINE")}
            />

            {/* Комментарий */}
            <div className="schedule_form_title" style={{ marginTop: 14 }}>
                Доп. комментарий
            </div>
            <textarea
                ref={commentRef}
                className="schedule_comment"
                placeholder="Укажите, откуда забрать товар или напишите иные комментарии"
                value={comment}
                onChange={(e) => {
                    setComment(e.target.value);
                    setCommentError("");
                }}
                aria-invalid={!!commentError}
            />
            {commentError && <div className="profile-error" style={{ marginTop: 8 }}>{commentError}</div>}

            <div className="auth-agree">
                <label className={`auth-check ${agreeError ? "is-error" : ""}`} >
                    <input
                        type="checkbox"
                        checked={agree}
                        onChange={(e) => {
                            setAgree(e.target.checked);
                            setAgreeError("");
                            setErr("");
                        }}
                        ref={agreeRef}
                    />
                    <span className="auth-check-ui"/>
                    <span className="auth-check-text">
                    Я согласен с{" "}
                        <span
                            className="auth-link"
                            onClick={() => window.open(docLinks.regulations, "_blank", "noopener,noreferrer")}
                            role="button"
                            tabIndex={0}
                        >
                      условиями и положениями
                    </span> и <span
                        className="auth-link"
                        onClick={() => window.open(docLinks.oferta, "_blank", "noopener,noreferrer")}
                        role="button"
                        tabIndex={0}
                    >
                      договором-офертой
                    </span>
                  </span>
                </label>
                {agreeError && <div className="auth-error">{agreeError}</div>}
            </div>
            {/* Actions */}
            <div className="profile-modal-actions profile-modal-actions--between" style={{ marginTop: 16 }}>
                <button className="btn-secondary" type="button" onClick={onClose} disabled={isSubmitting || isPaying}>
                    Отмена
                </button>

                <div className="profile-modal-actions-right">
                    <button
                        className="btn-secondary"
                        type="button"
                        onClick={() => handleSaveOrPay(false)}
                        disabled={isSubmitting || isPaying}
                    >
                        {isSubmitting ? <BtnSpinner/> : "Сохранить"}
                    </button>

                    {paymentOnline && (
                        <button
                            className="btn-primary"
                            type="button"
                            onClick={() => handleSaveOrPay(true)}
                            disabled={isSubmitting || isPaying}
                        >
                            {isPaying ? <BtnSpinner/> : "Оплатить сразу"}
                        </button>
                    )}
                </div>
            </div>
        </ModalShell>
    );
}
